import { router } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { Button, Divider, IconMail, PhoneNumberMaskInput, Text, XStack, tokens } from '@fhs/ui';
import { isPhoneValid } from '@fhs/ui/src/components/form-text-inputs/masks';

import { useAuthRouteBase } from '../auth-route-base-context';
import { useAuthScroll } from '../auth-scroll-context';
import { LegalDisclaimer } from '../components';
import { useCreateOtp } from '../hooks';
import { AuthIdentifiers } from '../types';

export const SignIn = () => {
  const { paths } = useAuthRouteBase();
  const { scrollToTop } = useAuthScroll();
  const { createOtp, otpLoading } = useCreateOtp();
  const [loginIdentifier, setLoginIdentifier] = useState<string>('');
  const [error, setError] = useState<boolean>(false);

  const onInputChange = useCallback(
    input => {
      setLoginIdentifier(input);
      setError(false);
    },
    [setLoginIdentifier, setError]
  );

  const attemptLogin = useCallback(async () => {
    if (isPhoneValid(loginIdentifier)) {
      await createOtp({
        loginIdentifier,
        identifierType: AuthIdentifiers.PHONE_NUMBER,
      });
    } else {
      setError(true);
      scrollToTop();
    }
  }, [createOtp, loginIdentifier, scrollToTop]);

  return (
    <>
      <PhoneNumberMaskInput
        label="Mobile Phone Number"
        value={loginIdentifier}
        onChange={onInputChange}
        error={error ? 'Please enter a valid phone number' : ''}
        autoFocus
        readOnly={otpLoading}
        onSubmitEditing={attemptLogin}
      />
      <LegalDisclaimer />
      <View style={{ width: '100%' }}>
        <Button
          size="md"
          style={[styles.continueButton]}
          disabledStyle={styles.disableButton}
          onPress={attemptLogin}
          loading={otpLoading}
          disabled={otpLoading}
        >
          <Button.Text>Continue</Button.Text>
        </Button>
        <XStack style={styles.dividerContainer}>
          <Divider />
          <Text style={styles.dividerText}>or</Text>
          <Divider />
        </XStack>
        <Button
          style={styles.continueButton}
          type={'outline-subtle'}
          size="md"
          onPress={() => {
            router.push(paths.continueWithEmail);
          }}
        >
          <Button.Icon color={tokens.colors.$blackOpacity75}>
            <IconMail />
          </Button.Icon>
          <Button.Text>Continue With Email</Button.Text>
        </Button>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  continueButton: {
    height: 48,
    width: '100%',
    marginHorizontal: 16,
    alignSelf: 'center',
  },
  disableButton: {
    backgroundColor: tokens.colors.$blackOpacity04,
    color: tokens.colors.$disabledText,
  },
  loadingButton: {
    color: tokens.colors.$disabledText,
  },
  dividerText: {
    color: tokens.colors.$disabledText,
    margin: 12,
  },
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
